import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { http, inr, computeTotals, requestErrorMessage } from "@/lib/erp";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { ArrowLeft, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { resolveDocumentText } from "@/lib/documentSettings";

const REASONS = ["Damaged", "Expired", "Wrong Product", "Excess Quantity", "Price Difference", "Other"];

export default function CreditNoteForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const invoiceId = new URLSearchParams(location.search).get("invoice");
  const [inv, setInv] = useState(null);
  const [items, setItems] = useState([]);
  const [reason, setReason] = useState("Damaged");
  const [mode, setMode] = useState("adjust");
  const [saving, setSaving] = useState(false);
  const [company, setCompany] = useState({});
  const [bankId, setBankId] = useState("__none__");
  const [invoiceLoading, setInvoiceLoading] = useState(Boolean(invoiceId));
  const [invoiceError, setInvoiceError] = useState("");
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [settingsError, setSettingsError] = useState("");

  const loadSettings = async () => {
    setSettingsLoading(true); setSettingsError("");
    try {
      const { data } = await http.get("/settings");
      setCompany(data || {});
      const banks = data?.banks || [];
      const defaultBank = banks.find((bank) => String(bank.id) === String(data?.default_bank_id));
      setBankId(defaultBank ? String(defaultBank.id) : "__none__");
    } catch (e) { setSettingsError(requestErrorMessage(e, "load company settings")); }
    finally { setSettingsLoading(false); }
  };
  const loadInvoice = async () => {
    if (!invoiceId) return;
    setInvoiceLoading(true); setInvoiceError("");
    try {
      const { data } = await http.get(`/invoices/${invoiceId}`);
      setInv(data);
      setItems(data.items.map((it) => ({ ...it, return_qty: it.qty, include: true })));
    } catch (e) { setInvoiceError(requestErrorMessage(e, "load invoice")); }
    finally { setInvoiceLoading(false); }
  };
  useEffect(() => { loadSettings(); loadInvoice(); }, [invoiceId]);

  const active = items.filter((it) => it.include && it.return_qty > 0)
    .map((it) => ({ ...it, qty: it.return_qty }));
  const t = useMemo(() => computeTotals(active, inv?.place_of_supply_code || "36", {}), [items, inv]);

  if (!invoiceId) return <div className="p-8 text-muted-foreground">Open a credit note from an invoice.</div>;
  if (!inv) return <div className="p-8 space-y-3">
    <div className={invoiceError ? "text-rose-600" : "text-muted-foreground"}>{invoiceError || (invoiceLoading ? "Loading invoice…" : "Invoice is unavailable.")}</div>
    {invoiceError && <Button size="sm" variant="outline" onClick={loadInvoice}>Retry invoice</Button>}
    {settingsError && <div className="flex items-center gap-3 text-rose-600"><span>{settingsError}</span><Button size="sm" variant="outline" onClick={loadSettings}>Retry company settings</Button></div>}
    {settingsLoading && <div className="text-muted-foreground">Loading company settings…</div>}
  </div>;

  const setItem = (i, patch) => setItems((arr) => arr.map((it, idx) => idx === i ? { ...it, ...patch } : it));

  const save = async () => {
    if (settingsLoading) return toast.error("Wait for company settings to load before saving.");
    if (settingsError) return toast.error(settingsError);
    if (active.length === 0) return toast.error("Select at least one item to return");
    setSaving(true);
    try {
      await http.post("/credit-notes", {
        invoice_id: inv.id, reason, mode,
        bank_id: bankId === "__none__" ? null : bankId,
        items: active.map((it) => ({ product_id: it.product_id, name: it.name, hsn: it.hsn, batch_no: it.batch_no, expiry_date: it.expiry_date, unit: it.unit, qty: it.return_qty, rate: it.rate, discount_pct: it.discount_pct, gst_rate: it.gst_rate })),
      });
      toast.success("Credit note created · stock returned");
      navigate("/sales/credit-notes");
    } catch (e) { toast.error(requestErrorMessage(e, "create credit note")); }
    finally { setSaving(false); }
  };

  const documentText = resolveDocumentText(company, null, "credit_note");
  const banks = company.banks || [];

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-6xl">
      <button onClick={() => navigate(`/sales/invoices/${inv.id}`)} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /> Back to invoice</button>
      <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Credit Note / Sales Return</h1>
      {settingsError && <div className="flex items-center justify-between gap-3 rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700"><span>{settingsError}</span><Button size="sm" variant="outline" onClick={loadSettings}>Retry company settings</Button></div>}
      {settingsLoading && <div className="text-sm text-muted-foreground">Loading company settings…</div>}
      <p className="text-sm text-muted-foreground">Against invoice <span className="font-mono font-semibold">{inv.invoice_no}</span> · {inv.customer_name}</p>

      <Card className="p-5 grid sm:grid-cols-3 gap-4">
        <div><Label htmlFor="cn-reason-select">Reason</Label><Select value={reason} onValueChange={setReason}><SelectTrigger id="cn-reason-select" className="mt-1.5 h-11" data-testid="cn-reason-select"><SelectValue /></SelectTrigger><SelectContent>{REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent></Select></div>
        <div><Label htmlFor="cn-mode-select">Settlement</Label><Select value={mode} onValueChange={setMode}><SelectTrigger id="cn-mode-select" className="mt-1.5 h-11" data-testid="cn-mode-select"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="adjust">Adjust against invoice</SelectItem><SelectItem value="refund">Cash refund</SelectItem></SelectContent></Select></div>
        <div>
          <Label htmlFor="credit-note-bank-select">Bank for this credit note</Label>
          <Select value={bankId} onValueChange={setBankId}>
            <SelectTrigger id="credit-note-bank-select" className="mt-1.5 h-11" data-testid="credit-note-bank-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">No bank</SelectItem>
              {banks.map((bank, index) => <SelectItem key={bank.id || index} value={String(bank.id)}>{bank.name || bank.bank_name || "Bank"}{bank.account_number || bank.bank_account ? ` · ${bank.account_number || bank.bank_account}` : ""}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </Card>

      {(documentText.notes || documentText.terms) && <Card className="p-5 text-sm space-y-2">
        <div className="flex justify-between items-center"><span className="font-medium">Shared document text</span><Link to="/settings" className="text-xs text-brand-600 hover:underline">Settings</Link></div>
        {documentText.notes && <div><div className="text-xs font-medium text-muted-foreground">Notes</div><p className="whitespace-pre-wrap">{documentText.notes}</p></div>}
        {documentText.terms && <div><div className="text-xs font-medium text-muted-foreground">Terms &amp; Conditions</div><p className="whitespace-pre-wrap">{documentText.terms}</p></div>}
      </Card>}

      <Card className="p-0 overflow-hidden">
        <div className="hidden md:block overflow-x-auto"><table className="w-full text-sm">
          <thead className="bg-slate-50"><tr className="text-left text-xs uppercase tracking-wider text-muted-foreground"><th className="px-3 py-2">Return</th><th className="px-3 py-2">Product</th><th className="px-3 py-2">Batch</th><th className="px-3 py-2 text-right">Sold Qty</th><th className="px-3 py-2 text-right">Return Qty</th><th className="px-3 py-2 text-right">Rate</th><th className="px-3 py-2 text-right">GST%</th></tr></thead>
          <tbody>{items.map((it, i) => (<tr key={i} className="border-t" data-testid={`cn-item-${i}`}>
            <td className="px-3 py-2"><input type="checkbox" checked={it.include} onChange={(e) => setItem(i, { include: e.target.checked })} data-testid={`cn-include-${i}`} /></td>
            <td className="px-3 py-2 font-medium">{it.name}</td>
            <td className="px-3 py-2 font-mono text-xs">{it.batch_no}</td>
            <td className="px-3 py-2 text-right">{it.qty}</td>
            <td className="px-3 py-2 text-right"><Input type="number" value={it.return_qty} onChange={(e) => setItem(i, { return_qty: Math.min(+e.target.value, it.qty) })} className="h-9 w-20 text-right" data-testid={`cn-qty-${i}`} /></td>
            <td className="px-3 py-2 text-right font-mono">{inr(it.rate)}</td>
            <td className="px-3 py-2 text-right">{it.gst_rate}%</td>
          </tr>))}</tbody>
        </table></div>
        <div className="space-y-3 p-3 md:hidden">
          {items.map((it, i) => (
            <section key={i} className={`rounded-lg border p-3 space-y-3 ${it.include ? "border-brand-200 bg-brand-50/40" : "border-border bg-card"}`} data-testid={`cn-item-card-${i}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-medium break-words">{it.name}</div>
                  <div className="mt-1 text-xs text-muted-foreground">{it.batch_no ? `Batch ${it.batch_no}` : "No batch"} · Sold {it.qty}</div>
                </div>
                <label htmlFor={`cn-include-mobile-${i}`} className="flex min-h-11 shrink-0 items-center gap-2 rounded-md px-2 text-sm cursor-pointer">
                  <input id={`cn-include-mobile-${i}`} type="checkbox" checked={it.include} onChange={(e) => setItem(i, { include: e.target.checked })} className="h-5 w-5 accent-brand-600" data-testid={`cn-include-${i}`} />
                  Return
                </label>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="block text-xs text-muted-foreground">Unit rate</span><span className="font-mono">{inr(it.rate)}</span></div>
                <div><span className="block text-xs text-muted-foreground">GST</span><span>{it.gst_rate}%</span></div>
                <div className="col-span-2">
                  <Label htmlFor={`cn-qty-mobile-${i}`} className="text-xs text-muted-foreground">Return quantity (max {it.qty})</Label>
                  <Input id={`cn-qty-mobile-${i}`} type="number" min="0" max={it.qty} value={it.return_qty} onChange={(e) => setItem(i, { return_qty: Math.max(0, Math.min(+e.target.value, it.qty)) })} className="mt-1 h-11 w-full text-right" data-testid={`cn-qty-${i}`} />
                </div>
              </div>
            </section>
          ))}
        </div>
      </Card>

      <Card className="p-4 sm:p-5 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
        <div className="text-sm text-muted-foreground">Taxable {inr(t.subtotal)} · Tax {inr(t.totalTax)}</div>
        <div className="font-heading text-lg sm:text-xl font-bold">Credit Total: <span data-testid="cn-total">{inr(t.grand)}</span></div>
      </Card>
      <div className="flex sticky bottom-0 z-10 bg-background/95 backdrop-blur py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:justify-end"><Button className="min-h-11 w-full sm:w-auto" onClick={save} disabled={saving || settingsLoading || !!settingsError} data-testid="save-credit-note-button"><Save className="h-4 w-4 mr-1" /> {saving ? "Saving…" : "Create Credit Note"}</Button></div>
    </div>
  );
}
